/**
 * ProInvitesPanel — admin "Send Pro by email" (invited = instantly Pro).
 *
 * Lives on /admin/gift-codes next to the code-based comps. Founder
 * types an email (+ months, default 12) → invitee gets a one-tap link
 * (/pro/claim?token=…) → account + Pro + session happen in one step.
 * No code to redeem, no grant button afterwards.
 *
 * English-only — admin surfaces are exempt from the i18n leak guard.
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Gift, Loader2, Send } from "lucide-react";
import { adminCreateProInvite, adminListProInvites } from "../lib/api";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
};

const daysUntil = (iso) => {
  try {
    return Math.ceil((new Date(iso) - Date.now()) / 86400000);
  } catch {
    return null;
  }
};

const StatusChip = ({ inv }) => {
  if (inv.expired)
    return (
      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#C4787C]/15 text-[#E29A9E] border border-[#C4787C]/30">
        Link expired
      </span>
    );
  if (inv.account_exists)
    return (
      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#D4B483]/15 text-[#D4B483] border border-[#D4B483]/30">
        Signed up — Pro not activated
      </span>
    );
  return (
    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/[0.04] text-white/50 border border-white/10">
      Invited
    </span>
  );
};

export const ProInvitesPanel = () => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [months, setMonths] = useState(12);
  const [sendEmail, setSendEmail] = useState(true);
  const [busy, setBusy] = useState(false);
  const [invites, setInvites] = useState(null);
  const [rowBusyId, setRowBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await adminListProInvites();
      setInvites(data.invites || []);
    } catch {
      setInvites([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await adminCreateProInvite({
        email: email.trim(),
        name: name.trim() || null,
        months: Number(months) || 12,
        send_email: sendEmail,
      });
      if (res.sent_email) {
        toast.success(`Pro gift emailed to ${res.email} — they're one tap away.`);
      } else {
        try {
          await navigator.clipboard.writeText(res.claim_url);
          toast.success(`Link copied — DM it to ${res.email}.`);
        } catch {
          toast.success(`Invite created for ${res.email} — copy the link below.`);
        }
      }
      setEmail("");
      setName("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't create Pro invite.");
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async (inv) => {
    try {
      await navigator.clipboard.writeText(inv.claim_url);
      toast.success(`Pro gift link for ${inv.email} copied`);
    } catch {
      toast.error("Couldn't copy — your browser blocked clipboard access.");
    }
  };

  const resend = async (inv) => {
    setRowBusyId(inv.invite_id);
    try {
      const res = await adminCreateProInvite({
        email: inv.email,
        send_email: true,
      });
      toast.success(
        res.sent_email
          ? `Pro gift re-emailed to ${inv.email}`
          : `Invite refreshed — email didn't send, copy the link instead`,
      );
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't resend invite.");
    } finally {
      setRowBusyId(null);
    }
  };

  return (
    <section
      className="bg-white/[0.03] border border-white/8 rounded-2xl p-5 sm:p-6 mb-6"
      data-testid="admin-pro-invites-section"
    >
      <header className="flex items-baseline justify-between gap-3 mb-1 flex-wrap">
        <h2 className="text-base font-medium text-white inline-flex items-center gap-2">
          <Gift className="h-4 w-4 text-[#D4B483]" /> Send Pro by email
        </h2>
        <span className="text-[10px] uppercase tracking-[0.22em] text-white/40">
          invited = instantly pro
        </span>
      </header>
      <p className="text-xs text-white/45 mb-4">
        They get a one-tap link — account + Pro happen in one step. Nothing
        for you to authorize afterwards, no code for them to redeem.
      </p>

      <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-[2fr_1.4fr_88px_auto] gap-3 items-end mb-2">
        <div>
          <label className="text-[10px] uppercase tracking-[0.22em] text-white/55 block mb-1.5">
            Email <span className="text-[#D4B483]">*</span>
          </label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="friend@email.com"
            className="w-full px-3 py-2.5 rounded-lg border border-white/10 bg-white/[0.03] text-white placeholder:text-white/30 focus:border-[#D4B483] focus:bg-white/[0.06] outline-none transition-colors"
            data-testid="admin-pro-invite-email"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.22em] text-white/55 block mb-1.5">
            Name
          </label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Catrine"
            className="w-full px-3 py-2.5 rounded-lg border border-white/10 bg-white/[0.03] text-white placeholder:text-white/30 focus:border-[#D4B483] focus:bg-white/[0.06] outline-none transition-colors"
            data-testid="admin-pro-invite-name"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.22em] text-white/55 block mb-1.5">
            Months
          </label>
          <input
            type="number"
            min={1}
            max={24}
            value={months}
            onChange={(e) => setMonths(e.target.value)}
            className="w-full px-3 py-2.5 rounded-lg border border-white/10 bg-white/[0.03] text-white focus:border-[#D4B483] focus:bg-white/[0.06] outline-none transition-colors"
            data-testid="admin-pro-invite-months"
          />
        </div>
        <button
          type="submit"
          disabled={busy || !email}
          className="inline-flex items-center justify-center gap-1.5 bg-[#D4B483] text-[#1A1720] hover:bg-[#E5C99A] disabled:opacity-50 px-5 py-2.5 rounded-full transition-colors text-sm font-medium"
          data-testid="admin-pro-invite-submit"
        >
          {busy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Send className="h-3.5 w-3.5" />
          )}
          {sendEmail ? "Send gift" : "Generate link"}
        </button>
      </form>
      <label className="inline-flex items-center gap-2 text-xs text-white/55 mb-5 cursor-pointer">
        <input
          type="checkbox"
          checked={sendEmail}
          onChange={(e) => setSendEmail(e.target.checked)}
          className="accent-[#D4B483]"
          data-testid="admin-pro-invite-send-email"
        />
        Email them now (uncheck to just copy a link)
      </label>

      {invites && invites.length > 0 && (
        <div className="space-y-2.5" data-testid="admin-pro-invites-pending">
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/40">
            Pending — {invites.length} not yet activated
          </div>
          {invites.map((inv) => {
            const d = daysUntil(inv.expires_at);
            return (
              <div
                key={inv.invite_id}
                className="bg-white/[0.04] border border-white/10 rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap"
                data-testid={`admin-pro-invite-row-${inv.invite_id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-white font-medium truncate">
                    {inv.name || inv.email}
                  </div>
                  <div className="text-xs text-white/55 truncate">
                    {inv.email} · {inv.months} months Pro
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-white/35 mt-1">
                    Invited {fmtDate(inv.created_at)}
                    {!inv.expired && d != null && ` · link expires in ${d}d`}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <StatusChip inv={inv} />
                  <button
                    onClick={() => copyLink(inv)}
                    className="inline-flex items-center gap-1.5 text-xs text-white/55 hover:text-white border border-white/10 hover:border-white/20 px-3 py-1.5 rounded-full transition-colors"
                    data-testid={`admin-pro-invite-copy-${inv.invite_id}`}
                  >
                    <Copy className="h-3 w-3" /> Copy link
                  </button>
                  <button
                    onClick={() => resend(inv)}
                    disabled={rowBusyId === inv.invite_id}
                    className="inline-flex items-center gap-1.5 bg-[#D4B483] text-[#1A1720] hover:bg-[#E5C99A] disabled:opacity-50 px-4 py-1.5 rounded-full transition-colors text-xs font-medium"
                    data-testid={`admin-pro-invite-resend-${inv.invite_id}`}
                  >
                    {rowBusyId === inv.invite_id ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Send className="h-3 w-3" />
                    )}
                    Resend email
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default ProInvitesPanel;
